(() => {
  let syncing = false;

  function getProp(item, key) {
    const props = item?.properties;
    if (!props) return null;
    if (Array.isArray(props)) {
      const found = props.find((entry) => {
        const name = entry?.[0] ?? entry?.name ?? entry?.key;
        return name === key;
      });
      return found ? found[1] ?? found.value ?? null : null;
    }
    if (Object.prototype.hasOwnProperty.call(props, key)) return props[key];
    for (const entry of Object.values(props)) {
      if (entry && typeof entry === 'object' && (entry.name === key || entry.key === key)) {
        return entry.value ?? entry[1] ?? null;
      }
    }
    return null;
  }

  function isHiddenAddon(item) {
    const title = `${item?.product_title || ''} ${item?.title || ''}`;
    if (/\[CFG\]/i.test(title)) return true;
    if (String(getProp(item, '_hidden') || '') === '1') return true;
    const layer = getProp(item, '_layer');
    const parentTitle = getProp(item, '_parent_title');
    return layer != null && parentTitle != null && String(layer) !== '' && String(parentTitle) !== '';
  }

  function getConfigId(item) {
    return getProp(item, '_config_id');
  }

  function getVisibleItemCount(cart) {
    return (cart?.items || []).reduce((sum, item) => {
      if (isHiddenAddon(item)) return sum;
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  function setVisibleCartCount(count) {
    window.__cfgVisibleCartCount = Number(count) || 0;
  }

  function formatMoney(cents) {
    const value = Number(cents) || 0;
    const format = window.themeVariables?.settings?.moneyFormat || '${{amount}}';
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(value, format);
    }

    const amount = (value / 100).toFixed(2);
    return format.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(value / 100)));
  }

  function getBundleTotals(cart) {
    const groups = {};
    (cart?.items || []).forEach((item) => {
      const configId = getConfigId(item);
      if (!configId) return;
      if (!groups[configId]) groups[configId] = 0;
      groups[configId] += Number(item.final_line_price) || 0;
    });
    return groups;
  }

  function ensureTpoIgnore(el) {
    if (!el) return;
    el.querySelectorAll('price-list, sale-price, [data-config-sale-price]').forEach((node) => {
      node.classList.add('tpo_ignore');
    });
  }

  function writeSalePrice(salePrice, total) {
    if (!salePrice) return;
    salePrice.classList.add('tpo_ignore');
    const srOnly = salePrice.querySelector('.sr-only');
    const srHtml = srOnly ? srOnly.outerHTML : '';
    const next = `${srHtml}${formatMoney(total)}`;
    if (salePrice.innerHTML !== next) {
      salePrice.innerHTML = next;
    }
  }

  function patchConfigLinePrices(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const groups = getBundleTotals(cart);

    document.querySelectorAll('line-item[data-config-role="parent"][data-config-id]').forEach((el) => {
      const configId = el.getAttribute('data-config-id');
      if (!configId || groups[configId] == null) return;

      const total = groups[configId];
      el.setAttribute('data-config-bundle-price', String(total));
      ensureTpoIgnore(el);

      const salePrice = el.querySelector('[data-config-sale-price], sale-price');
      writeSalePrice(salePrice, total);
    });
  }

  let lastPatchedCart = null;
  let patchObserver = null;
  let patchingPrices = false;
  let patchObserverTimer = null;

  function schedulePatchConfigLinePrices(cart) {
    if (cart) lastPatchedCart = cart;
    if (!lastPatchedCart) return;

    const run = () => {
      patchingPrices = true;
      try {
        patchConfigLinePrices(lastPatchedCart);
      } finally {
        queueMicrotask(() => {
          patchingPrices = false;
        });
      }
    };
    queueMicrotask(run);
    // Impact cart-drawer replaces HTML on cart:change via setTimeout(0|1250).
    // Easify Product Options may rewrite sale-price shortly after drawer open.
    setTimeout(run, 0);
    setTimeout(run, 50);
    setTimeout(run, 300);
    setTimeout(run, 1300);
  }

  function observeConfigLinePriceMutations() {
    if (patchObserver || typeof MutationObserver === 'undefined') return;

    patchObserver = new MutationObserver(() => {
      if (patchingPrices || !lastPatchedCart) return;
      clearTimeout(patchObserverTimer);
      patchObserverTimer = setTimeout(() => {
        patchingPrices = true;
        try {
          patchConfigLinePrices(lastPatchedCart);
        } finally {
          queueMicrotask(() => {
            patchingPrices = false;
          });
        }
      }, 0);
    });

    const start = (root) => {
      if (!root) return;
      try {
        patchObserver.observe(root, {
          childList: true,
          subtree: true,
          characterData: true
        });
      } catch (_error) {
        // no-op
      }
    };

    start(document.querySelector('cart-drawer'));
    start(document.getElementById('main'));

    // Re-apply when the drawer opens (Easify often rewrites prices on open).
    document.addEventListener(
      'toggle',
      (event) => {
        const drawer =
          event.target?.closest?.('cart-drawer') ||
          (event.target?.tagName === 'CART-DRAWER' ? event.target : null);
        if (!drawer) return;
        if (event.target === drawer && drawer.open === false) return;
        start(drawer);
        schedulePatchConfigLinePrices(lastPatchedCart);
      },
      true
    );
  }

  function applyVisibleItemCount(cart) {
    if (!cart || !Array.isArray(cart.items)) return cart;
    const visible = getVisibleItemCount(cart);
    cart.item_count = visible;
    setVisibleCartCount(visible);
    return cart;
  }

  function syncCartCountElements(count) {
    const next = Number(count) || 0;
    setVisibleCartCount(next);
    document.querySelectorAll('cart-count').forEach((el) => {
      try {
        el.textContent = String(next);
        if (next === 0) {
          el.classList.add('opacity-0');
          el.style.opacity = '0';
        } else {
          el.classList.remove('opacity-0');
          el.style.opacity = '1';
        }
      } catch (_error) {
        // no-op
      }
    });
  }

  async function fetchCart() {
    return (await fetch(`${Shopify.routes.root}cart.js`, { credentials: 'same-origin' })).json();
  }

  async function syncVisibleCartCountFromServer() {
    try {
      const cart = applyVisibleItemCount(await fetchCart());
      syncCartCountElements(cart.item_count);
      return cart;
    } catch (_error) {
      return null;
    }
  }

  async function applyUpdates(updates) {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections }
      })
    );

    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates, sections })
    });

    if (!response.ok) return null;
    return response.json();
  }

  async function removeOrphanConfiguratorAddons(cart) {
    const currentCart = cart || (await fetchCart());
    const updates = {};
    let needsUpdate = false;

    (currentCart.items || []).forEach((item) => {
      // Standalone CFG addons (no configurator bundle id) must not remain in cart.
      if (isHiddenAddon(item) && !getConfigId(item)) {
        updates[item.key] = 0;
        needsUpdate = true;
        return;
      }
      if (/\[CFG\]/i.test(`${item.product_title || ''} ${item.title || ''}`) && !getConfigId(item)) {
        updates[item.key] = 0;
        needsUpdate = true;
      }
    });

    if (!needsUpdate) return { cart: currentCart, changed: false };

    const cartContent = await applyUpdates(updates);
    if (!cartContent) return { cart: currentCart, changed: false };

    applyVisibleItemCount(cartContent);
    syncCartCountElements(cartContent.item_count);

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'configurator-sync',
          cart: cartContent
        }
      })
    );

    return { cart: cartContent, changed: true };
  }

  async function reconcileConfigBundles(cart) {
    if (syncing) return false;

    syncing = true;
    try {
      const orphanResult = await removeOrphanConfiguratorAddons(cart);
      const currentCart = orphanResult.cart || cart || (await fetchCart());
      const groups = {};

      (currentCart.items || []).forEach((item) => {
        const configId = getConfigId(item);
        if (!configId) return;
        if (!groups[configId]) groups[configId] = { parents: [], addons: [] };
        if (isHiddenAddon(item)) groups[configId].addons.push(item);
        else groups[configId].parents.push(item);
      });

      const updates = {};
      let needsUpdate = false;

      Object.keys(groups).forEach((configId) => {
        const group = groups[configId];
        const parentQty = group.parents.reduce((sum, item) => sum + item.quantity, 0);

        group.addons.forEach((addon) => {
          const targetQty = parentQty;
          if (addon.quantity !== targetQty) {
            updates[addon.key] = targetQty;
            needsUpdate = true;
          }
        });
      });

      if (!needsUpdate) {
        applyVisibleItemCount(currentCart);
        syncCartCountElements(currentCart.item_count);
        return orphanResult.changed;
      }

      const cartContent = await applyUpdates(updates);
      if (!cartContent) return false;
      applyVisibleItemCount(cartContent);
      syncCartCountElements(cartContent.item_count);

      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
        return true;
      }

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'configurator-sync',
            cart: cartContent
          }
        })
      );
      return true;
    } finally {
      syncing = false;
    }
  }

  function patchCartCountClass(CartCount) {
    if (!CartCount?.prototype || CartCount.prototype.__cfgCountPatched) return;
    CartCount.prototype.__cfgCountPatched = true;

    const descriptor = Object.getOwnPropertyDescriptor(CartCount.prototype, 'itemCount');
    if (!descriptor?.set || !descriptor?.get) return;

    Object.defineProperty(CartCount.prototype, 'itemCount', {
      configurable: true,
      enumerable: descriptor.enumerable,
      get() {
        return descriptor.get.call(this);
      },
      set(count) {
        // Prefer SSR / synced visible count so theme pageshow cannot flash raw item_count.
        const visible =
          typeof window.__cfgVisibleCartCount === 'number' ? window.__cfgVisibleCartCount : Number(count) || 0;
        descriptor.set.call(this, visible);
      }
    });

    CartCount.prototype._updateFromServer = async function patchedUpdateFromServer() {
      await syncVisibleCartCountFromServer();
    };
  }

  document.addEventListener(
    'cart:change',
    (event) => {
      if (!event.detail?.cart) return;
      applyVisibleItemCount(event.detail.cart);
    },
    true
  );

  document.addEventListener('cart:change', (event) => {
    if (!event.detail?.cart) return;
    applyVisibleItemCount(event.detail.cart);
    syncCartCountElements(event.detail.cart.item_count);
    schedulePatchConfigLinePrices(event.detail.cart);
  });

  document.addEventListener('cart:refresh', () => {
    syncVisibleCartCountFromServer().then((cart) => {
      if (cart) schedulePatchConfigLinePrices(cart);
    });
  });

  window.addEventListener('pageshow', () => {
    // Keep __cfgVisibleCartCount authoritative; refresh from server in background.
    if (typeof window.__cfgVisibleCartCount === 'number') {
      syncCartCountElements(window.__cfgVisibleCartCount);
    }
    syncVisibleCartCountFromServer().then((cart) => {
      if (cart) schedulePatchConfigLinePrices(cart);
    });
  });

  customElements.whenDefined('cart-count').then(() => {
    patchCartCountClass(customElements.get('cart-count'));
    if (typeof window.__cfgVisibleCartCount === 'number') {
      syncCartCountElements(window.__cfgVisibleCartCount);
    }
  });

  document.addEventListener('line-item:change', (event) => {
    if (syncing) return;
    reconcileConfigBundles(event.detail?.cart);
  });

  document.addEventListener('cart:change', (event) => {
    if (syncing) return;
    if (event.detail?.baseEvent === 'configurator-sync') return;
    reconcileConfigBundles(event.detail?.cart);
  });

  function bootConfigCartSync() {
    observeConfigLinePriceMutations();
    reconcileConfigBundles().then(() =>
      syncVisibleCartCountFromServer().then((cart) => cart && schedulePatchConfigLinePrices(cart))
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootConfigCartSync);
  } else {
    bootConfigCartSync();
  }
})();